import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";
import { SIGNUPS_ENABLED } from "@/config/featureFlags";

type AuthModalMode = "login" | "signup";

interface AuthModalOptions {
  /** Path to navigate to after a successful signup (or login). */
  redirectAfterSuccess?: string;
}

interface AuthModalContextType {
  isAuthModalOpen: boolean;
  authModalMode: AuthModalMode;
  redirectAfterSuccess: string | null;
  openAuthModal: (mode: AuthModalMode, options?: AuthModalOptions) => void;
  closeAuthModal: () => void;
  switchAuthMode: () => void;
  showVerifyEmailModal: boolean;
  verifyEmailAddress: string;
  verifyEmailStartCooldown: boolean;
  openVerifyEmailModal: (email: string, startCooldown?: boolean) => void;
  closeVerifyEmailModal: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode>("login");
  const [redirectAfterSuccess, setRedirectAfterSuccess] = useState<string | null>(null);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [verifyEmailAddress, setVerifyEmailAddress] = useState("");
  const [verifyEmailStartCooldown, setVerifyEmailStartCooldown] = useState(false);

  const openAuthModal = useCallback((mode: AuthModalMode, options?: AuthModalOptions) => {
    // Wind-down: signups are closed, so any "signup" request opens login instead.
    // Single chokepoint — covers every call site (navbar, anonymous gate, etc.).
    setAuthModalMode(SIGNUPS_ENABLED ? mode : "login");
    setRedirectAfterSuccess(options?.redirectAfterSuccess ?? null);
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
    setRedirectAfterSuccess(null);
  }, []);

  const switchAuthMode = useCallback(() => {
    // Never switch into signup while signups are closed.
    setAuthModalMode((prev) => (prev === "login" && SIGNUPS_ENABLED ? "signup" : "login"));
  }, []);

  const openVerifyEmailModal = useCallback((email: string, startCooldown = false) => {
    setVerifyEmailAddress(email);
    setVerifyEmailStartCooldown(startCooldown);
    setShowVerifyEmailModal(true);
  }, []);

  const closeVerifyEmailModal = useCallback(() => {
    setShowVerifyEmailModal(false);
  }, []);

  const value = useMemo(() => ({
    isAuthModalOpen,
    authModalMode,
    redirectAfterSuccess,
    openAuthModal,
    closeAuthModal,
    switchAuthMode,
    showVerifyEmailModal,
    verifyEmailAddress,
    verifyEmailStartCooldown,
    openVerifyEmailModal,
    closeVerifyEmailModal,
  }), [
    isAuthModalOpen,
    authModalMode,
    redirectAfterSuccess,
    openAuthModal,
    closeAuthModal,
    switchAuthMode,
    showVerifyEmailModal,
    verifyEmailAddress,
    verifyEmailStartCooldown,
    openVerifyEmailModal,
    closeVerifyEmailModal,
  ]);

  return (
    <AuthModalContext.Provider value={value}>
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error("useAuthModal must be used within an AuthModalProvider");
  }
  return context;
}
